import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTaReportApi } from '../config';
import { useAttendancePreset } from '../config/useAttendancePreset';
import { parseTableParams } from '../../../utils/Utils';
import { buildReportLabels, statusReportLabels } from '../../../utils/reportLabels';
import DataTable from '../../../components/ui/DataTable';
import ReportExportButtons from '../../../components/ui/ReportExportButtons';
import PresetFilterBanner from './PresetFilterBanner';

// Tek kaydırma: şerit sabit yükseklik, tablo kalan alanı alır (min-height: 0).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// PDKS gün raporu listesi (Geç Kalma / Erken Çıkma / Devamsızlık / Eksik Çalışanlar / Vardiya Dışı / Anomali): Devam
// Paneli'nden açılınca panelin süzgeçleri adresten okunur ve her tablo isteğine yapışkan olarak eklenir; üstte "süzgeçler
// uygulanıyor" şeridi. AKM POC §7.1: araç çubuğunda sunucu tarafı Excel / PDF (TAM veri, o anki süzgeçler — sayfa değil);
// yerleşik "yalnız ekrandaki sayfa" CSV'si kapalı.
export default function PresetReportPage({ fetchFn, titleKey, exportUrl }) {
  const { t } = useTranslation();
  const { preset, clearPreset } = useAttendancePreset();
  const {
    columns, data, fetching, pagination, filterList, setFilterList, setQueryParams, queryParams, applyPreset, statusLabels,
  } = useTaReportApi(fetchFn, { preset });

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(applyPreset(parseTableParams(tableState, columns)));
  }, [columns, setFilterList, setQueryParams, applyPreset]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, [setFilterList]);

  const getLabels = useCallback(
    () => buildReportLabels(t, columns, t(titleKey), statusReportLabels(statusLabels)),
    [t, columns, titleKey, statusLabels],
  );

  const toolbarActions = useMemo(() => (exportUrl
    ? <ReportExportButtons url={exportUrl} params={queryParams} getLabels={getLabels} />
    : null), [exportUrl, queryParams, getLabels]);

  return (
    <Container>
      <PresetFilterBanner preset={preset} queryParams={queryParams} onClear={clearPreset} />
      <TableArea>
        {useMemo(() => (
          <DataTable
            title={t(titleKey)}
            fetching={fetching}
            columns={columns}
            data={data}
            totalRowCount={pagination.totalRowCount}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            filterList={filterList}
            onFilterConfirm={handleFilterConfirm}
            onTableChanged={handleDataStateChange}
            toolbarActions={toolbarActions}
            download={false}
          />
        ), [t, titleKey, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange, toolbarActions])}
      </TableArea>
    </Container>
  );
}
